import { useEffect } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { useLenis } from "@/lib/useLenis";
import { applySeo, applyStructuredData } from "@/lib/seo";
import { Nav } from "@/components/landing/Nav";
import { CookieBanner, openCookiePreferences } from "@/components/CookieBanner";
import { trackConversion } from "@/lib/ads";
import { Instagram, MessageCircle, Stethoscope } from "lucide-react";
import { SITEMAP, CONTACT } from "@/content/site";

function Footer() {
  return (
    <footer data-testid="footer" className="bg-graphite text-canvas pt-20 pb-12">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <img src="/logo-mark-light.png" alt="Logo Dott. Alessandro Valle" className="h-12 w-auto mb-5" />
            <p className="text-[11px] uppercase tracking-[0.5em] text-sage mb-2">Dott.</p>
            <p className="font-serif text-4xl md:text-5xl font-light tracking-tight">Alessandro <span className="italic text-sage">Valle</span></p>
            <p className="mt-2 text-xs uppercase tracking-[0.2em] text-canvas/45">Medico Chirurgo</p>
            <p className="mt-5 max-w-sm text-sm text-canvas/55 leading-relaxed">
              Struttura medica tecnologica guidata dal Dott. Alessandro Valle: trattamenti laser,
              Skin Quality e tricologia, con staff dedicato ai percorsi corpo. Studio medico a Genova.
            </p>
            <Link to="/contatti" className="mt-8 inline-flex items-center overflow-hidden group relative border border-canvas/40 px-7 py-3 text-xs uppercase tracking-[0.2em]">
              <span className="absolute inset-0 bg-canvas translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
              <span className="relative group-hover:text-graphite transition-colors duration-500">Prenota una valutazione</span>
            </Link>
            <div className="mt-8 space-y-1.5 text-sm text-canvas/60">
              {CONTACT.studios.map((s) => <p key={s}>{s}</p>)}
              {CONTACT.hours && <p className="text-canvas/50">Orari: {CONTACT.hours.label}</p>}
              <a href={`mailto:${CONTACT.email}`} className="block hover:text-canvas link-underline w-fit pt-1">{CONTACT.email}</a>
              {CONTACT.phones.map((p) => <a key={p.href} href={`tel:${p.href}`} onClick={() => trackConversion("call")} data-testid="footer-phone" className="block hover:text-canvas link-underline w-fit">Tel. {p.label}</a>)}
            </div>
            <div className="mt-6 space-y-1 text-xs text-canvas/40 leading-relaxed">
              <p>{CONTACT.legal.ordine} · Iscrizione Albo n. {CONTACT.legal.albo}</p>
              <p>P.IVA {CONTACT.legal.piva} · C.F. {CONTACT.legal.cf}</p>
              <p>Il Dott. Valle esercita presso {CONTACT.legal.clinic}, Genova.</p>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-canvas/70">
              <a href={CONTACT.social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram" className="inline-flex items-center gap-2 hover:text-canvas transition-colors">
                <Instagram className="w-4 h-4" /> Instagram
              </a>
              <a href={CONTACT.social.miodottore} target="_blank" rel="noopener noreferrer" aria-label="MioDottore" data-testid="footer-miodottore" className="inline-flex items-center gap-2 hover:text-canvas transition-colors">
                <Stethoscope className="w-4 h-4" /> MioDottore
              </a>
              <a href={`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(CONTACT.whatsappMsg)}`} onClick={() => trackConversion("whatsapp")} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" data-testid="footer-whatsapp" className="inline-flex items-center gap-2 hover:text-canvas transition-colors">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </div>
          </div>
          <div className="lg:col-span-4 lg:col-start-8">
            <p className="text-[11px] uppercase tracking-[0.2em] text-canvas/40 mb-5">Percorsi</p>
            <div className="grid grid-cols-2 gap-x-8 gap-y-2.5">
              {SITEMAP.map((l) => (
                <Link key={l.to} to={l.to} className="text-sm text-canvas/70 hover:text-canvas link-underline w-fit">{l.label}</Link>
              ))}
            </div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-canvas/40 mt-8 mb-4">Note legali</p>
            <div className="flex flex-col gap-2.5 items-start">
              <Link to="/privacy" data-testid="footer-privacy-link" className="text-sm text-canvas/70 hover:text-canvas link-underline">Privacy Policy</Link>
              <Link to="/cookie-policy" data-testid="footer-cookie-link" className="text-sm text-canvas/70 hover:text-canvas link-underline">Cookie Policy</Link>
              <button data-testid="footer-cookie-preferences" onClick={openCookiePreferences} className="text-sm text-canvas/70 hover:text-canvas link-underline">Preferenze cookie</button>
            </div>
          </div>
        </div>
        <div className="mt-16 pt-8 border-t border-canvas/15 flex flex-col gap-2 text-xs text-canvas/45">
          <p>Le informazioni presenti sul sito hanno finalità esclusivamente divulgativa e non sostituiscono la visita medica. Indicazioni, controindicazioni, risultati e numero di sedute vengono definiti dopo una valutazione individuale. Le persone raffigurate nelle immagini non sono pazienti dello studio.</p>
          <span className="mt-2">© {new Date().getFullYear()} Dott. Alessandro Valle · Genova, Italia</span>
        </div>
      </div>
    </footer>
  );
}

export function Layout({ children }) {
  useLenis();
  const { pathname } = useLocation();
  useEffect(() => {
    applyStructuredData(CONTACT);
  }, []);
  useEffect(() => {
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    }
    window.scrollTo(0, 0);
    applySeo(pathname);
  }, [pathname]);

  return (
    <div className="App bg-canvas min-h-screen">
      <div className="grain-overlay" />
      <Toaster position="bottom-right" theme="dark" />
      <Nav />
      <main>{children ?? <Outlet />}</main>
      <Footer />
      <CookieBanner />
    </div>
  );
}
